import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Tabs, 
  Table, 
  Tag, 
  Space, 
  Button, 
  message, 
  Descriptions, 
  Progress, 
  Statistic,
  Row,
  Col,
  Alert,
  Popconfirm
} from 'antd';
import { 
  SettingOutlined, 
  DatabaseOutlined, 
  MonitorOutlined, 
  ReloadOutlined,
  ClearOutlined,
  RedoOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { systemAPI } from '../services/api';

const { TabPane } = Tabs;

const SystemSettings = ({ isSuperAdmin }) => {
  const [systemInfo, setSystemInfo] = useState(null);
  const [appConfig, setAppConfig] = useState(null);
  const [databaseStatus, setDatabaseStatus] = useState(null);
  const [_logFiles, setLogFiles] = useState([]);
  const [_loading, setLoading] = useState(false);

  // 检查权限
  if (!isSuperAdmin) {
    return (
      <Alert
        message="权限不足"
        description="您需要超级管理员权限才能访问系统设置"
        type="warning"
        showIcon
      />
    );
  }

  // 获取系统信息
  const fetchSystemInfo = async () => {
    try {
      setLoading(true);
      const response = await systemAPI.getSystemInfo();
      setSystemInfo(response.data);
    } catch {
      message.error('获取系统信息失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取应用配置
  const fetchAppConfig = async () => {
    try {
      const response = await systemAPI.getAppConfig();
      setAppConfig(response.data);
    } catch {
      message.error('获取应用配置失败');
    }
  };

  // 获取数据库状态
  const fetchDatabaseStatus = async () => {
    try {
      const response = await systemAPI.getDatabaseStatus();
      setDatabaseStatus(response.data);
    } catch {
      message.error('获取数据库状态失败');
    }
  };

  // 获取日志文件
  const fetchLogFiles = async () => {
    try {
      const response = await systemAPI.getLogFiles();
      setLogFiles(response.data);
    } catch {
      message.error('获取日志文件失败');
    }
  };

  // 清理缓存
  const handleClearCache = async () => {
    try {
      await systemAPI.clearCache();
      message.success('缓存清理完成');
    } catch {
      message.error('清理缓存失败');
    }
  };

  // 重启应用
  const handleRestart = async () => {
    try {
      await systemAPI.restartApp();
      message.success('应用将在3秒后重启');
    } catch {
      message.error('重启应用失败');
    }
  };

  useEffect(() => {
    fetchSystemInfo();
    fetchAppConfig();
    fetchDatabaseStatus();
    fetchLogFiles();
  }, []);

  // 格式化字节大小
  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // 格式化时间
  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${days}天 ${hours}小时 ${minutes}分钟`;
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card title={<><SettingOutlined /> 系统设置</>} extra={
        <Space>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={() => {
              fetchSystemInfo();
              fetchAppConfig();
              fetchDatabaseStatus();
              fetchLogFiles();
            }}
          >
            刷新
          </Button>
        </Space>
      }>
        <Tabs defaultActiveKey="system">
          {/* 系统信息 */}
          <TabPane tab={<><MonitorOutlined /> 系统信息</>} key="system">
            {systemInfo && (
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Card size="small" title="基本信息">
                    <Descriptions column={1} size="small">
                      <Descriptions.Item label="操作系统">{systemInfo.platform || 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="架构">{systemInfo.arch || 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="Node版本">{systemInfo.nodeVersion || 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="运行时间">{formatUptime(systemInfo.uptime || 0)}</Descriptions.Item>
                      <Descriptions.Item label="主机名">{systemInfo.hostname || 'N/A'}</Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card size="small" title="内存使用">
                    <Statistic 
                      title="内存使用率" 
                      value={systemInfo.memory?.usage || 0} 
                      suffix="%" 
                      precision={2}
                    />
                    <Progress 
                      percent={parseFloat(systemInfo.memory?.usage || 0)} 
                      status={(systemInfo.memory?.usage || 0) > 80 ? 'exception' : 'normal'}
                    />
                    <div style={{ marginTop: 8 }}>
                      <div>总内存: {formatBytes(systemInfo.memory?.total || 0)}</div>
                      <div>已使用: {formatBytes(systemInfo.memory?.used || 0)}</div>
                      <div>可用: {formatBytes(systemInfo.memory?.free || 0)}</div>
                    </div>
                  </Card>
                </Col>
                <Col span={24}>
                  <Card size="small" title="CPU信息">
                    <Descriptions column={2} size="small">
                      <Descriptions.Item label="核心数">{systemInfo.cpu?.cores || 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="型号">{systemInfo.cpu?.model || 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="负载" span={2}>
                        {(systemInfo.loadAverage || []).map((load, index) => (
                          <Tag key={index} color="blue">{load.toFixed(2)}</Tag>
                        ))}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
              </Row>
            )}
          </TabPane>

          {/* 应用配置 */}
          <TabPane tab={<><InfoCircleOutlined /> 应用配置</>} key="config">
            {appConfig && (
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Card size="small" title="服务器配置">
                    <Descriptions column={1} size="small">
                      <Descriptions.Item label="端口">{appConfig.server?.port || 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="主机">{appConfig.server?.host || 'N/A'}</Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card size="small" title="数据库配置">
                    <Descriptions column={1} size="small">
                      <Descriptions.Item label="主机">{appConfig.database?.host || 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="端口">{appConfig.database?.port || 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="数据库">{appConfig.database?.name || 'N/A'}</Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
                <Col span={24}>
                  <Card size="small" title="其他配置">
                    <Descriptions column={2} size="small">
                      <Descriptions.Item label="JWT过期时间">{appConfig.jwt?.expiresIn || 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="会话过期时间">{appConfig.session?.maxAge ? `${appConfig.session.maxAge}ms` : 'N/A'}</Descriptions.Item>
                      <Descriptions.Item label="CORS来源" span={2}>
                        {(appConfig.cors?.origins || []).map((origin, index) => (
                          <Tag key={index} color="green">{origin}</Tag>
                        ))}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
              </Row>
            )}
          </TabPane>

          {/* 数据库状态 */}
          <TabPane tab={<><DatabaseOutlined /> 数据库状态</>} key="database">
            {databaseStatus && (
              <div>
                <Alert
                  message={(databaseStatus?.connected || false) ? "数据库连接正常" : "数据库连接异常"}
                  type={(databaseStatus?.connected || false) ? "success" : "error"}
                  icon={(databaseStatus?.connected || false) ? <CheckCircleOutlined /> : <WarningOutlined />}
                  style={{ marginBottom: 16 }}
                />
                <Card size="small" title="数据库统计">
                  <Statistic 
                    title="总表数" 
                    value={databaseStatus?.totalTables || 0} 
                    suffix="个"
                  />
                  <Statistic 
                    title="总大小" 
                    value={formatBytes(databaseStatus?.totalSize || 0)} 
                  />
                </Card>
                <Table
                  dataSource={databaseStatus?.tables || []}
                  columns={[
                    { title: '表名', dataIndex: 'TABLE_NAME', key: 'name' },
                    { title: '行数', dataIndex: 'TABLE_ROWS', key: 'rows' },
                    { 
                      title: '大小', 
                      key: 'size',
                      render: (_, record) => formatBytes(record.TOTAL_SIZE || 0)
                    }
                  ]}
                  pagination={false}
                  size="small"
                />
              </div>
            )}
          </TabPane>

          {/* 系统操作 */}
          <TabPane tab={<><SettingOutlined /> 系统操作</>} key="operations">
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Card size="small" title="缓存管理">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Popconfirm
                      title="确定要清理缓存吗？"
                      onConfirm={handleClearCache}
                      okText="确定"
                      cancelText="取消"
                    >
                      <Button 
                        icon={<ClearOutlined />} 
                        type="primary"
                        danger
                      >
                        清理缓存
                      </Button>
                    </Popconfirm>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      清理系统缓存和临时文件
                    </div>
                  </Space>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" title="应用管理">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Popconfirm
                      title="确定要重启应用吗？应用将在3秒后重启！"
                      onConfirm={handleRestart}
                      okText="确定"
                      cancelText="取消"
                    >
                      <Button 
                        icon={<RedoOutlined />} 
                        type="primary"
                        danger
                      >
                        重启应用
                      </Button>
                    </Popconfirm>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      重启整个应用程序
                    </div>
                  </Space>
                </Card>
              </Col>
            </Row>
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default SystemSettings;
